// frontend/src/ts/protocols/persistence/uploadTypes.ts

export type UploadState =
  | 'found'
  | 'copied'
  | 'pending'
  | 'uploading'
  | 'persisted'
  | 'dead'

export interface PendingUpload {
  id: number
  state: UploadState
  foundPath: string
  path: string | null
  filename: string
  fileSize: number
  uploadProgress: number
  remoteFileId: string | null
  attemptCount: number
  lastAttemptAt: number | null
  lastError: string | null
  createdAt: number
  /** File's filesystem creation time (ms since epoch). The watcher's
   *  age check (`watch_worker.rs`) uses this as the basis: it copies
   *  the file once `now - sourceCreatedAt >= minFileAgeSeconds *
   *  1000`. May be null on filesystems that don't expose creation
   *  time. */
  sourceCreatedAt?: number
  /** Per-source min-age gate (seconds) at the time the watcher
   *  inserted this row. UI countdown for 'found'-state rows. */
  minFileAgeSeconds?: number
  /**
   * Step to associate the uploaded file with. Set by the watcher from
   * the active worker handle at enqueue-time, or by the picker for
   * explicit per-row targeting. The upload worker prefers this over
   * the worker handle's `protocolStepId` at associate-time.
   */
  protocolStepId?: string
}

/**
 * Seconds remaining until the watcher will copy this row. Returns 0
 * for non-'found' states, rows without a `sourceCreatedAt` (the
 * filesystem didn't expose it), or rows that have already exceeded
 * the age gate. Matches the watcher's age check at
 * `watch_worker.rs:281` (uses `meta.created()` as the basis).
 *
 * Edge case: if `sourceCreatedAt` is in the future (clock skew
 * between insert and read, or a misbehaving filesystem), the
 * "elapsed" is negative and the raw formula returns a value
 * greater than `minFileAgeSeconds`. Clamp both ends: never
 * negative, never greater than `minFileAgeSeconds`. (The UI
 * should show the same number the user expects to see given
 * the configured gate.)
 */
export function secondsUntilCopy(
    row: PendingUpload,
    nowMs: number = Date.now(),
): number {
    if (row.state !== 'found') return 0
    if (row.sourceCreatedAt == null || row.minFileAgeSeconds == null) return 0
    const elapsedSec = (nowMs - row.sourceCreatedAt) / 1000
    if (elapsedSec <= 0) {
        // sourceCreatedAt is in the future relative to now — the
        // full gate is still ahead. Show the full configured value.
        return row.minFileAgeSeconds
    }
    const remaining = row.minFileAgeSeconds - elapsedSec
    return Math.max(0, Math.ceil(remaining))
}

export interface UploadSummary {
  found: number
  copied: number
  pending: number
  uploading: number
  persisted: number
  dead: number
}

/**
 * One watched source. The UI lets the user add multiple of these in
 * a single Start Data Collection action, each with its own
 * directory, recursion flag, extension allowlist, file-age
 * threshold, and backup-on-copy flag.
 */
export interface WatcherSourceConfig {
  dirPath: string
  recursive: boolean
  extensions: string[]
  minFileAgeSeconds: number
  backupSourceOnCopy: boolean
}

export interface WatcherConfig {
  sources: WatcherSourceConfig[]
}

export interface DataCollectionStatus {
  watcherRunning: boolean
  uploadWorkerRunning: boolean
  watcherConfig: WatcherConfig | null
}